import { alpha, type ColorSystemOptions, darken, lighten } from "@mui/material";

// Helper functions to generate color variations
function generateColorShades(mainColor: string) {
  return {
    D30: darken(mainColor, 0.3),
    D20: darken(mainColor, 0.2),
    D10: darken(mainColor, 0.1),
    L10: lighten(mainColor, 0.1),
    L20: lighten(mainColor, 0.2),
    L30: lighten(mainColor, 0.3),
    L40: lighten(mainColor, 0.4),
    L50: lighten(mainColor, 0.5),
    L60: lighten(mainColor, 0.6),
    L70: lighten(mainColor, 0.7),
    L80: lighten(mainColor, 0.8),
    L90: lighten(mainColor, 0.9),
  };
}

function createGradientBackground(mainColor: string): string {
  const l90Color = lighten(mainColor, 0.9);
  return `linear-gradient(180deg, #FFFFFF 3.16%, ${l90Color} 97.53%)`;
}

// Type definitions for color palettes
type GeneratedShades = ReturnType<typeof generateColorShades>;

type MaterialShades = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  A100: string;
  A200: string;
  A400: string;
  A700: string;
};

type BaseColorPalette = {
  main: string;
  gradient: {
    background: string;
  };
} & GeneratedShades;

type ExtendedColorPalette = BaseColorPalette & {
  light: string;
  dark: string;
};

type FullColorPalette = ExtendedColorPalette & MaterialShades;

// Factory functions
const createBaseColorPalette = (main: string): BaseColorPalette => ({
  main,
  gradient: {
    background: createGradientBackground(main),
  },
  ...generateColorShades(main),
});

const createExtendedColorPalette = (
  main: string,
  light: string,
  dark: string,
): ExtendedColorPalette => ({
  main,
  light,
  dark,
  gradient: {
    background: createGradientBackground(main),
  },
  ...generateColorShades(main),
});

const createFullColorPalette = (
  main: string,
  light: string,
  dark: string,
  shades: MaterialShades,
): FullColorPalette => ({
  main,
  light,
  dark,
  gradient: {
    background: createGradientBackground(main),
  },
  ...shades,
  ...generateColorShades(main),
});

const TEXT_PRIMARY = "#1E2232";
const ACTION_ACTIVE = "#52566D";

export const TEXT_COLORS: {
  primary: string;
  secondary: string;
  disabled: string;
} = {
  primary: TEXT_PRIMARY,
  secondary: alpha(TEXT_PRIMARY, 0.6),
  disabled: alpha(TEXT_PRIMARY, 0.38),
};

export const PRIMARY_COLORS: FullColorPalette = createFullColorPalette(
  "#5366FF", // main - Mapped to 400
  "#A2A8FF", // light - Mapped to 200
  "#233FEF", // dark - Mapped to 600
  {
    50: "#E9EAFF",
    100: "#C8CAFF",
    200: "#A2A8FF",
    300: "#7785FF",
    400: "#5366FF",
    500: "#2848FB",
    600: "#233FEF",
    700: "#1233E2",
    800: "#0025D8",
    900: "#0001BF",
    A100: "#82B1FF",
    A200: "#448AFF",
    A400: "#2979FF",
    A700: "#2962FF",
  },
);

export const SECONDARY_COLORS: FullColorPalette = createFullColorPalette(
  "#767C9C", // main - Mapped to 300
  "#E7E8EE", // light - Mapped to 50
  "#3F467E", // dark - Mapped to 500
  {
    50: "#E7E8EE",
    100: "#C2C5D5",
    200: "#9BA0B9",
    300: "#767C9C",
    400: "#5B6189",
    500: "#3F467E",
    600: "#393F75",
    700: "#31366B",
    800: "#292D60",
    900: "#1A1D4E",
    A100: "#CFD8DC",
    A200: "#B0BEC5",
    A400: "#78909C",
    A700: "#455A64",
  },
);

export const ACCENT_COLORS: ExtendedColorPalette = createExtendedColorPalette(
  "#10E8A0",
  "#3FECB3",
  "#0BA270",
);

export const YELLOWC_COLORS: BaseColorPalette =
  createBaseColorPalette("#FFB81C");

export const REDC_COLORS: BaseColorPalette = createBaseColorPalette("#FF585D");

export const GREENC_COLORS: BaseColorPalette =
  createBaseColorPalette("#18AD86");

export const VIOLETC_COLORS: BaseColorPalette =
  createBaseColorPalette("#8120FF");

export const ORANGEC_COLORS: BaseColorPalette =
  createBaseColorPalette("#EA8000");

export const PINKC_COLORS: BaseColorPalette = createBaseColorPalette("#D47DDB");

export const S800_COLORS: BaseColorPalette = createBaseColorPalette(
  SECONDARY_COLORS[800],
);

export const ACTION_COLORS: {
  active: string;
  hover: string;
  selected: string;
  focus: string;
  disabled: string;
  disabledBackground: string;
} = {
  active: alpha(ACTION_ACTIVE, 0.56),
  hover: alpha(ACTION_ACTIVE, 0.04),
  selected: alpha(ACTION_ACTIVE, 0.08),
  focus: alpha(ACTION_ACTIVE, 0.12),
  disabled: alpha(ACTION_ACTIVE, 0.38),
  disabledBackground: alpha(ACTION_ACTIVE, 0.12),
};

export const SUPPORT_COLORS: {
  main: string;
  dark: string;

  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
} = {
  main: "#D1D4E2", // Mapped to 500
  dark: "#B5B8C4", // Mapped to 600

  50: "#FAFBFC",
  100: "#f6f6f9",
  200: "#F1F2F6",
  300: "#ECEEF3",
  400: "#E8E9F0",
  500: "#D1D4E2",
  600: "#B5B8C4",
};

export const CUSTOM_PALETTE: ColorSystemOptions["palette"] = {
  divider: SUPPORT_COLORS.main,
  text: TEXT_COLORS,
  primary: PRIMARY_COLORS,
  secondary: SECONDARY_COLORS,
  support: SUPPORT_COLORS,
  accent: ACCENT_COLORS,
  yellowc: YELLOWC_COLORS,
  redc: REDC_COLORS,
  greenc: GREENC_COLORS,
  violetc: VIOLETC_COLORS,
  orangec: ORANGEC_COLORS,
  pinkc: PINKC_COLORS,
  s800: S800_COLORS,
  action: ACTION_COLORS,
  gradient: {
    outline: `linear-gradient(78deg, #4F6EF9 5.99%, ${ACCENT_COLORS.main} 104.08%)`,
    background: `linear-gradient(204deg, #4F6EF9 17.9%, ${PRIMARY_COLORS[500]} 87.14%)`,
  },
  subtone: "#1B35FF",
};

// linear-gradient(180deg, var(--common-white_states-main, #FFF) 3.16%, var(--L90, #EDEFFF) 97.53%)

export type CustomColors =
  | "primary"
  | "secondary"
  | "accent"
  | "s800"
  | "yellowc"
  | "redc"
  | "greenc"
  | "violetc"
  | "orangec"
  | "pinkc";
